import { useState } from "react";
import { Button } from "@tellescope/ui";
import { arrayMove } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { PageItem } from "./PageItem";
import { ChildPageItem } from "./ChildPageItem";
import type { Page } from "../../../shared/schema";

interface PageListProps {
  pages: Page[];
  selectedPageId: string | null;
  onSelectPage: (pageId: string | null) => void;
  onAddPage: () => void;
  onRemovePage: (pageId: string) => void;
  onReorderPages: (pageIds: string[]) => void;
  onAddChildPage: (parentId: string) => void;
  onRemoveChildPage: (parentId: string, childId: string) => void;
  onReorderChildPages: (parentId: string, childIds: string[]) => void;
}

export const PageList: React.FC<PageListProps> = ({
  pages,
  selectedPageId,
  onSelectPage,
  onAddPage,
  onRemovePage,
  onReorderPages,
  onAddChildPage,
  onRemoveChildPage,
  onReorderChildPages,
}) => {
  const { t } = useTranslation();

  // Track which page is being dragged, and which parent scope it belongs to (top-level = null)
  const [draggedPageId, setDraggedPageId] = useState<string | null>(null);
  const [draggedParentId, setDraggedParentId] = useState<string | null>(null);
  const [expandedPageIds, setExpandedPageIds] = useState<Set<string>>(() => new Set(pages.map((p) => p.id)));

  const toggleExpanded = (pageId: string) => {
    setExpandedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  };

  const handleDragStart = (pageId: string, parentId: string | null) => {
    setDraggedPageId(pageId);
    setDraggedParentId(parentId);
  };

  const handleDragOver = (e: React.DragEvent, targetPageId: string, parentId: string | null) => {
    e.preventDefault();
    // Only allow reordering within the same scope (top-level list, or the same parent's children)
    if (!draggedPageId || draggedPageId === targetPageId || draggedParentId !== parentId) return;

    if (parentId === null) {
      const fromIndex = pages.findIndex((p) => p.id === draggedPageId);
      const toIndex = pages.findIndex((p) => p.id === targetPageId);
      if (fromIndex === -1 || toIndex === -1) return;

      const newOrder = arrayMove(pages, fromIndex, toIndex);
      onReorderPages(newOrder.map((p) => p.id));
      return;
    }

    const parent = pages.find((p) => p.id === parentId);
    const children = parent?.pages ?? [];
    const fromIndex = children.findIndex((c) => c.id === draggedPageId);
    const toIndex = children.findIndex((c) => c.id === targetPageId);
    if (fromIndex === -1 || toIndex === -1) return;

    const newOrder = arrayMove(children, fromIndex, toIndex);
    onReorderChildPages(
      parentId,
      newOrder.map((c) => c.id)
    );
  };

  const handleDragEnd = () => {
    setDraggedPageId(null);
    setDraggedParentId(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">{t("Pages")}</h3>
        <Button variant="ghost" size="sm" onClick={onAddPage}>
          <Plus className="h-4 w-4 mr-1" />
          {t("Add")}
        </Button>
      </div>

      <div className="space-y-1">
        {pages.map((page, index) => {
          const children = page.pages ?? [];
          const isExpanded = expandedPageIds.has(page.id);

          return (
            <div key={page.id}>
              <PageItem
                page={page}
                isFirst={index === 0}
                isSelected={selectedPageId === page.id}
                isDragging={draggedPageId === page.id}
                canDelete={pages.length > 1}
                onSelect={() => onSelectPage(selectedPageId === page.id ? null : page.id)}
                onRemove={() => onRemovePage(page.id)}
                onDragStart={() => handleDragStart(page.id, null)}
                onDragOver={(e) => handleDragOver(e, page.id, null)}
                onDragEnd={handleDragEnd}
                isExpanded={isExpanded}
                onToggleExpanded={() => toggleExpanded(page.id)}
                onAddChildPage={() => {
                  setExpandedPageIds((prev) => new Set(prev).add(page.id));
                  onAddChildPage(page.id);
                }}
              />

              {isExpanded && children.length > 0 && (
                <div className="space-y-1 mt-1">
                  {children.map((child) => (
                    <ChildPageItem
                      key={child.id}
                      page={child}
                      isSelected={selectedPageId === child.id}
                      isDragging={draggedPageId === child.id}
                      onSelect={() => onSelectPage(selectedPageId === child.id ? null : child.id)}
                      onRemove={() => onRemoveChildPage(page.id, child.id)}
                      onDragStart={() => handleDragStart(child.id, page.id)}
                      onDragOver={(e) => handleDragOver(e, child.id, page.id)}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pages.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">{t("No pages yet. Add one to get started.")}</p>
      )}
    </div>
  );
};
